import { MapPin, Envelope, ArrowUpRight } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { PageLayout } from "@/components/layout";

import gokayPhoto from "@/assets/images/team/gokay-saldamli.png";
import jacksonPhoto from "@/assets/images/team/jackson-blazensky.png";

import logoOregonState from "@/assets/images/logos/oregon-state.svg";
import logoSjsu from "@/assets/images/logos/sjsu.svg";
import logoUcsb from "@/assets/images/logos/ucsb.png";
import logoHpe from "@/assets/images/logos/hpe.svg";
import logoSamsung from "@/assets/images/logos/samsung.svg";
import logoTheGraph from "@/assets/images/logos/the-graph.png";

import alexisPhoto from "@/assets/images/team/alexis-asseman.png";
import onurPhoto from "@/assets/images/team/onur-soysal.png";
import gustavoPhoto from "@/assets/images/team/gustavo-inacio.png";
import carlosPhoto from "@/assets/images/team/carlos-vargas.png";
import bryanPhoto from "@/assets/images/team/bryan-cole.png";
import nelsPhoto from "@/assets/images/team/nels-chantarotwong.png";
import shahoodPhoto from "@/assets/images/team/shahood-khan.png";
import jordanPhoto from "@/assets/images/team/jordan-rein.png";
import tarangPhoto from "@/assets/images/team/tarang-gala.png";
import johnPhoto from "@/assets/images/team/john-corrigan.png";

type CropStyle = { width: string; height: string; left: string; top: string };

const founders: { name: string; role: string; photo: string; crop: CropStyle }[] = [
	{ name: "Gokay Saldamli", role: "Co-Founder & CEO", photo: gokayPhoto, crop: { width: "100%", height: "142.75%", left: "-0.73%", top: "-5.29%" } },
	{ name: "Jackson Blazensky", role: "Co-Founder & CTO", photo: jacksonPhoto, crop: { width: "297.19%", height: "396.25%", left: "-114.22%", top: "-144.38%" } },
];

const institutions = [
	[
		{ name: "Oregon State University", logo: logoOregonState, w: 100, h: 32 },
		{ name: "San Jose State University", logo: logoSjsu, w: 73, h: 30 },
		{ name: "UC Santa Barbara", logo: logoUcsb, w: 78, h: 32 },
	],
	[
		{ name: "Hewlett Packard Enterprise", logo: logoHpe, w: 77, h: 32 },
		{ name: "Samsung", logo: logoSamsung, w: 120, h: 19 },
		{ name: "The Graph", logo: logoTheGraph, w: 62, h: 32 },
	],
];

const team: { name: string; role: string; photo: string; crop?: CropStyle }[] = [
	{ name: "Alexis Asseman", role: "Engineering Lead", photo: alexisPhoto, crop: { width: "123.75%", height: "165%", left: "-7.57%", top: "-17.51%" } },
	{ name: "Onur Soysal", role: "Engineering", photo: onurPhoto, crop: { width: "100%", height: "145.87%", left: "0%", top: "-36.46%" } },
	{ name: "Gustavo Inacio", role: "Engineering", photo: gustavoPhoto, crop: { width: "127.38%", height: "190.84%", left: "-12.09%", top: "-23.86%" } },
	{ name: "Carlos Vargas", role: "Engineering", photo: carlosPhoto, crop: { width: "100%", height: "102.57%", left: "0%", top: "-1.29%" } },
	{ name: "Bryan Cole", role: "Engineering", photo: bryanPhoto },
	{ name: "Nels Chantarotwong", role: "QA Testing", photo: nelsPhoto },
	{ name: "Shahood Khan", role: "Engineering", photo: shahoodPhoto, crop: { width: "114.51%", height: "214.22%", left: "-15.36%", top: "-60.36%" } },
	{ name: "Jordan Rein", role: "Product Lead", photo: jordanPhoto },
	{ name: "Tarang Gala", role: "Design Lead", photo: tarangPhoto },
	{ name: "John Corrigan", role: "Product", photo: johnPhoto, crop: { width: "117.62%", height: "130.74%", left: "-0.6%", top: "-22.92%" } },
];

export default function AboutRoute() {
	return (
		<PageLayout activeRoute="/about">
			{/* Hero */}
			<section className="pt-20 md:pt-32 pb-16 md:pb-24 px-9 md:px-[8%] lg:px-[15%]">
				<motion.h1
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
					className="text-[36px] md:text-[56px] lg:text-[72px] font-normal tracking-tight text-white leading-[1.05] max-w-4xl"
				>
					We're building the<br />
					infrastructure for a<br />
					<span className="text-white/40">permanent internet.</span>
				</motion.h1>
			</section>

			{/* Mission */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-24 md:pb-32">
				<div className="grid md:grid-cols-2 gap-16 md:gap-24">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
					>
						<p className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-5">Mission</p>
						<p className="text-[20px] md:text-[24px] text-white/70 leading-relaxed font-normal">
							The internet was supposed to be decentralized. Somewhere along the way, a few companies captured it. Basilica is the course correction — public infrastructure that no single entity can own, shut down, or enshittify.
						</p>
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
					>
						<p className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-5">Founded</p>
						<p className="text-[64px] md:text-[80px] font-normal tracking-tight text-white leading-none mb-4">2024</p>
						<p className="text-[17px] text-white/45 leading-relaxed">
							Santa Cruz, CA & Austin, TX. Built by a team with experience at The Graph, IBM, Dropbox, PlayStation, Intel, and Google.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Leadership */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-24 md:pb-32">
				<motion.p
					initial={{ opacity: 0, y: 12 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
					className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-12"
				>
					Leadership
				</motion.p>
				<div className="grid md:grid-cols-2 gap-12 md:gap-16">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
						className="flex flex-col justify-between"
					>
						<div>
							<p className="text-[20px] md:text-[24px] text-white/70 leading-relaxed font-light mb-6">
								Gokay Saldamli and Jackson Blazensky are experts in zk-SNARKs, VDFs, delay encryption, and Homomorphic Encryption.
							</p>
							<p className="text-[17px] text-white/45 leading-relaxed">
								Former core devs at The Graph, they co-created Roadrunner — a record-holding polynomial commitment scheme with Justin Thaler.
							</p>
						</div>
						<div className="mt-10 flex flex-col gap-5 opacity-50">
							{institutions.map((row, ri) => (
								<div key={ri} className="flex items-center gap-8">
									{row.map((inst) => (
										<img
											key={inst.name}
											src={inst.logo}
											alt={inst.name}
											style={{ width: inst.w * 0.7, height: inst.h * 0.7 }}
											className="brightness-0 invert opacity-70 shrink-0"
										/>
									))}
								</div>
							))}
						</div>
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
						className="flex justify-center gap-8 md:gap-12"
					>
						{founders.map((founder, i) => (
							<motion.div
								key={founder.name}
								initial={{ opacity: 0, scale: 0.95 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true }}
								transition={{ duration: 0.5, delay: 0.15 + i * 0.1, ease: [0.25, 1, 0.5, 1] }}
								className="group flex flex-col items-center text-center"
							>
								<div className="relative w-[140px] h-[140px] md:w-[180px] md:h-[180px] mb-5 rounded-full overflow-hidden ring-1 ring-white/[0.08] group-hover:ring-white/[0.16] transition-all duration-300">
									<img
										src={founder.photo}
										alt={founder.name}
										className="absolute max-w-none grayscale group-hover:grayscale-0 transition-[filter] duration-500"
										style={founder.crop}
									/>
								</div>
								<h3 className="text-[18px] md:text-[20px] text-white font-normal tracking-tight">{founder.name}</h3>
								<p className="text-[14px] text-white/35 mt-1">{founder.role}</p>
							</motion.div>
						))}
					</motion.div>
				</div>
			</section>

			{/* Team */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-24 md:pb-32">
				<motion.p
					initial={{ opacity: 0, y: 12 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
					className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-12"
				>
					Team
				</motion.p>
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-10">
					{team.map((person, i) => (
						<motion.div
							key={person.name}
							initial={{ opacity: 0, y: 16 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.4, delay: i * 0.05, ease: [0.25, 1, 0.5, 1] }}
							className="group flex flex-col items-center text-center"
						>
							<div className="relative w-[120px] h-[120px] mb-5 rounded-full overflow-hidden ring-1 ring-white/[0.08] group-hover:ring-white/[0.16] transition-all duration-300">
								<img
									src={person.photo}
									alt={person.name}
									className={`grayscale group-hover:grayscale-0 transition-[filter] duration-500 ${person.crop ? "absolute max-w-none" : "w-full h-full object-cover"}`}
									style={person.crop}
								/>
							</div>
							<h3 className="text-[16px] text-white font-normal tracking-tight leading-tight">{person.name}</h3>
							<p className="text-[13px] text-white/35 mt-1">{person.role}</p>
						</motion.div>
					))}
				</div>
			</section>

			{/* Office / Map */}
			<section className="px-9 md:px-[8%] lg:px-[15%] pb-24 md:pb-32">
				<motion.p
					initial={{ opacity: 0, y: 12 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
					className="text-[14px] uppercase tracking-[0.2em] text-white/30 mb-12"
				>
					Office
				</motion.p>
				<div className="grid md:grid-cols-[1fr_360px] gap-10 md:gap-12">
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
						className="relative rounded-2xl overflow-hidden border border-white/[0.06] aspect-[16/9] md:aspect-auto md:min-h-[400px]"
					>
						<iframe
							title="Basilica office location"
							src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d75000!2d-122.0308!3d36.9741!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e4!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
							className="absolute inset-0 w-full h-full border-0 grayscale invert opacity-80"
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
						/>
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
						className="flex flex-col justify-between"
					>
						<div>
							<div className="flex items-center gap-2 text-white/40 mb-4">
								<MapPin size={18} weight="fill" />
								<span className="text-[14px] uppercase tracking-[0.2em]">Santa Cruz, CA & Austin, TX</span>
							</div>
							<p className="text-[28px] md:text-[32px] font-normal tracking-tight text-white leading-tight mb-4">
								Where the redwoods meet the Pacific.
							</p>
							<p className="text-[17px] text-white/45 leading-relaxed">
								We chose Santa Cruz because the best thinking happens away from the noise. Close enough to Silicon Valley to stay connected, far enough to stay focused.
							</p>
						</div>
						<a
							href="mailto:hello@basilica.dev"
							className="group mt-8 inline-flex items-center gap-3 text-white/60 hover:text-white transition-colors duration-200"
						>
							<Envelope size={20} />
							<span className="text-[15px]">hello@basilica.dev</span>
							<ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
						</a>
					</motion.div>
				</div>
			</section>
		</PageLayout>
	);
}
